// Запросы и действия официального контура.
//
// ## Почему после действия идёт сброс, а не подкладывание ответа в кэш
//
// Ответы действий несут `grid` целиком, и подложить его в кэш было бы на один запрос дешевле.
// Но сетка кэшируется вместе с отбором (`?f.state`, `?q`), а сервер собирает `grid` без отбора:
// подложив её под ключ с отбором, мы показали бы человеку строки, которые он отфильтровал.
// Сброс дороже на один запрос, зато числа на экране всегда согласованы между собой — а в
// контуре на экране одновременно видны к выдаче, выплачено, заведено и остаток, и разъехавшаяся
// четвёрка читается как ошибка расчёта.
//
// Мигания при этом нет: прошлый срез остаётся на экране, пока едет свежий (правило 11).
//
// ## Предпросмотр закрытия грузится ЗАРАНЕЕ
//
// Иначе нажатие «Закрыть период» открывает пустое окно и человек ждёт в нём числа, ради которых
// окно и открыл. Один лёгкий запрос при открытии экрана убирает это ожидание совсем.

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  addTranche,
  applyWrits,
  closePeriod,
  createPeriod,
  createWritOrder,
  fetchClosePreview,
  fetchGrid,
  fetchEmployeeCircuit,
  fetchPeriods,
  fetchReopenPreview,
  fetchWritOrders,
  matchNames,
  payTranche,
  payTranches,
  refuseCarry,
  reopenPeriod,
  reverseTranche,
  setRowState,
  setRowWrit,
  setStates,
  upsertRows,
  type BulkPayOut,
  type BulkStateOut,
  type EmployeeCircuitOut,
  type CloseOut,
  type ClosePreviewOut,
  type MatchLineIn,
  type MatchOut,
  type OfficialGridOut,
  type OfficialPeriodOut,
  type OfficialRowOut,
  type ReopenOut,
  type ReopenPreviewOut,
  type RowIn,
  type RowsLoadedOut,
  type TrancheIn,
  type TrancheOut,
  type WritOrderIn,
  type WritOrderOut,
} from '@/shared/api/endpoints/official';
import type { ApiError } from '@/shared/errors/api-error';
import type { ListResponse } from '@/shared/api/types';
import { подтверждение } from '@/shared/ui/toast/подтверждение';

export interface GridFilter {
  readonly state?: string;
  readonly q?: string;
}

export const officialKeys = {
  все: ['official'] as const,
  периоды: (year: number | undefined) => ['official', 'periods', year ?? 'все'] as const,
  сетка: (periodId: string, отбор: GridFilter) =>
    ['official', 'grid', periodId, отбор.state ?? '', отбор.q ?? ''] as const,
  предпросмотрЗакрытия: (periodId: string) => ['official', 'close-preview', periodId] as const,
  предпросмотрОткрытия: (periodId: string) => ['official', 'reopen-preview', periodId] as const,
  документы: (employeeId: string | undefined, activeOnly: boolean) =>
    ['official', 'writ-orders', employeeId ?? 'все', activeOnly] as const,
  человек: (employeeId: string) => ['official', 'employee', employeeId] as const,
};

/**
 * Реестр периодов. `включено` гасит запрос у того, кому контур не показывается: официальная
 * часть полевым ролям не видна вовсе, и стучаться за ней в сервер, зная, что он откажет, —
 * лишний круг по сети и лишняя строка в журнале отказов.
 */
export function usePeriods(
  year?: number,
  включено = true,
): UseQueryResult<ListResponse<OfficialPeriodOut>> {
  return useQuery({
    queryKey: officialKeys.периоды(year),
    queryFn: ({ signal }) => fetchPeriods(year === undefined ? {} : { year }, { signal }),
    enabled: включено,
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Карточка человека в официальном контуре: история периодов, транши, документы, остатки.
 *
 * Тз, раздел 5.4.5, называет её среди экранов контура. Дверь была и работала, а из программы её
 * никто не звал: официальная история человека собиралась глазами, переключением периодов в
 * шапке и поиском фамилии в каждом.
 */
export function useEmployeeCircuit(
  employeeId: string,
): UseQueryResult<EmployeeCircuitOut, ApiError> {
  return useQuery<EmployeeCircuitOut, ApiError>({
    queryKey: officialKeys.человек(employeeId),
    queryFn: ({ signal }) => fetchEmployeeCircuit(employeeId, { signal }),
  });
}

export function useGrid(
  periodId: string | null,
  отбор: GridFilter,
  включено = true,
): UseQueryResult<OfficialGridOut> {
  return useQuery({
    queryKey: officialKeys.сетка(periodId ?? 'нет', отбор),
    queryFn: ({ signal }) =>
      fetchGrid(
        periodId!,
        { state: отбор.state, q: отбор.q === '' ? undefined : отбор.q },
        { signal },
      ),
    enabled: periodId !== null && включено,
    // Прошлая сетка остаётся на экране, пока едет следующая: спиннер на месте уже показанной
    // суммы запрещён (правило 11).
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Предпросмотр закрытия. Грузится, пока период открыт, а не по нажатию: числа обязаны быть в
 * окне подтверждения сразу, и они же говорят, что закрытию мешает.
 */
export function useClosePreview(
  periodId: string | null,
  включено: boolean,
): UseQueryResult<ClosePreviewOut> {
  return useQuery({
    queryKey: officialKeys.предпросмотрЗакрытия(periodId ?? 'нет'),
    queryFn: ({ signal }) => fetchClosePreview(periodId!, { signal }),
    enabled: periodId !== null && включено,
    placeholderData: (прежние) => прежние,
  });
}

export function useReopenPreview(
  periodId: string | null,
  включено: boolean,
): UseQueryResult<ReopenPreviewOut> {
  return useQuery({
    queryKey: officialKeys.предпросмотрОткрытия(periodId ?? 'нет'),
    queryFn: ({ signal }) => fetchReopenPreview(periodId!, { signal }),
    enabled: periodId !== null && включено,
    placeholderData: (прежние) => прежние,
  });
}

export function useWritOrders(
  employeeId: string | undefined,
  activeOnly = false,
): UseQueryResult<ListResponse<WritOrderOut>> {
  return useQuery({
    queryKey: officialKeys.документы(employeeId, activeOnly),
    queryFn: ({ signal }) =>
      fetchWritOrders({ employee_id: employeeId, active_only: activeOnly }, { signal }),
    placeholderData: (прежние) => прежние,
  });
}

/**
 * Сброс после действия. Гасится всё дерево контура: транш меняет и строку, и итоги периода, и
 * панель проверки, и предпросмотр закрытия — они обязаны обновиться вместе.
 */
function useСброс() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: officialKeys.все });
  };
}

// ⚠️ У ТРЁХ действий этого экрана подтверждения НЕТ, и это решение, а не забывчивость.
//
// Заведение периода, пересчёт процентов приставов и смена состояния у пачки строк отвечают
// человеку САМИ, полосой над сеткой, и отвечают подробнее любого всплывающего окна: «Период
// 09.2026 заведён и открыт», «Процент проставлен: строк изменилось 12», «Состояние „выплачено“
// присвоено 80 строкам. Не тронуто 3: Старцев — переплата».
//
// Всплывающее подтверждение рядом с такой полосой — это один факт, рассказанный дважды и
// разными словами, причём короткий вариант вытесняет подробный. Добавлено 04.09.2026 и убрано
// в тот же день при перечитывании своих же фраз.
//
// По той же причине молчат и ОДИНОЧНЫЕ правки строки — процент приставов, состояние, загрузка
// строк из файла: результат каждой стоит в самой ячейке, на которую человек и смотрит.
// Подтверждения НЕТ у всех действий этого модуля, и это записано здесь один раз, чтобы не
// повторять у каждого.

export function useCreatePeriod() {
  const сбросить = useСброс();
  return useMutation<OfficialPeriodOut, unknown, { year: number; month: number }>({
    mutationFn: (body) => createPeriod(body),
    onSuccess: сбросить,
  });
}

export function useUpsertRows(periodId: string) {
  const сбросить = useСброс();
  return useMutation<RowsLoadedOut, unknown, RowIn[]>({
    mutationFn: (rows) => upsertRows(periodId, rows),
    onSuccess: сбросить,
  });
}

/** Сопоставление фамилий. Ничего не записывает, поэтому сброса после него НЕТ. */
export function useMatchNames(periodId: string) {
  return useMutation<MatchOut, unknown, MatchLineIn[]>({
    mutationFn: (lines) => matchNames(periodId, lines),
    meta: подтверждение('Фамилии сопоставлены'),
  });
}

export function useApplyWrits(periodId: string) {
  const сбросить = useСброс();
  return useMutation<OfficialGridOut, unknown, void>({
    mutationFn: () => applyWrits(periodId),
    onSuccess: сбросить,
  });
}

export function useSetRowWrit() {
  const сбросить = useСброс();
  return useMutation<
    OfficialRowOut,
    unknown,
    { rowId: string; percent: string | null; version: number }
  >({
    mutationFn: ({ rowId, percent, version }) => setRowWrit(rowId, percent, version),
    onSuccess: сбросить,
  });
}

export function useSetRowState() {
  const сбросить = useСброс();
  return useMutation<OfficialRowOut, unknown, { rowId: string; state: string; version: number }>({
    // МОЛЧИТ намеренно: см. объяснение в шапке модуля.
    mutationFn: ({ rowId, state, version }) => setRowState(rowId, state, version),
    onSuccess: сбросить,
  });
}

/**
 * Массовая смена состояния строк (тз, раздел 5.4.5: «фильтр по состоянию … массовая отметка»).
 *
 * Пропущенные приезжают ПОИМЁННО и показываются словами: «состояние уже такое» — не ошибка, но
 * человек, выделивший восемьдесят строк, обязан знать, скольких из них действие не коснулось.
 */
export function useSetStates() {
  const сбросить = useСброс();
  return useMutation<BulkStateOut, unknown, { periodId: string; rowIds: string[]; state: string }>({
    // МОЛЧИТ намеренно: см. объяснение в шапке модуля.
    mutationFn: ({ periodId, rowIds, state }) => setStates(periodId, rowIds, state),
    onSuccess: сбросить,
  });
}

export function useAddTranche() {
  const сбросить = useСброс();
  return useMutation<
    TrancheOut,
    unknown,
    { rowId: string; body: TrancheIn; idempotencyKey: string }
  >({
    mutationFn: ({ rowId, body, idempotencyKey }) => addTranche(rowId, body, idempotencyKey),
    meta: подтверждение('Транш добавлен'),
    onSuccess: сбросить,
  });
}

export function usePayTranche() {
  const сбросить = useСброс();
  return useMutation<TrancheOut, unknown, { trancheId: string; paidAt?: string | null }>({
    mutationFn: ({ trancheId, paidAt }) => payTranche(trancheId, paidAt),
    meta: подтверждение('Транш отмечен выплаченным'),
    onSuccess: сбросить,
  });
}

export function usePayTranches(periodId: string) {
  const сбросить = useСброс();
  return useMutation<BulkPayOut, unknown, { trancheIds: string[]; paidAt?: string | null }>({
    mutationFn: ({ trancheIds, paidAt }) => payTranches(periodId, trancheIds, paidAt),
    meta: подтверждение<{ trancheIds: string[] }>(({ trancheIds }) =>
      trancheIds.length === 1 ? 'Транш отмечен выплаченным' : `Траншей отмечено: ${trancheIds.length}`,
    ),
    onSuccess: сбросить,
  });
}

export function useReverseTranche() {
  const сбросить = useСброс();
  return useMutation<TrancheOut, unknown, { trancheId: string; basis: string }>({
    mutationFn: ({ trancheId, basis }) => reverseTranche(trancheId, basis),
    meta: подтверждение('Выплата транша отменена'),
    onSuccess: сбросить,
  });
}

export function useRefuseCarry() {
  const сбросить = useСброс();
  return useMutation<OfficialRowOut, unknown, { rowId: string; basis: string; version: number }>({
    mutationFn: ({ rowId, basis, version }) => refuseCarry(rowId, basis, version),
    meta: подтверждение('Отказ от переноса записан'),
    onSuccess: сбросить,
  });
}

export function useClosePeriod(periodId: string) {
  const сбросить = useСброс();
  return useMutation<CloseOut, unknown, number>({
    mutationFn: (version) => closePeriod(periodId, version),
    meta: подтверждение('Официальный период закрыт'),
    onSuccess: сбросить,
  });
}

export function useReopenPeriod(periodId: string) {
  const сбросить = useСброс();
  return useMutation<ReopenOut, unknown, { version: number; basis: string }>({
    mutationFn: ({ version, basis }) => reopenPeriod(periodId, version, basis),
    meta: подтверждение('Официальный период открыт заново'),
    onSuccess: сбросить,
  });
}

export function useCreateWritOrder() {
  const сбросить = useСброс();
  return useMutation<WritOrderOut, unknown, WritOrderIn>({
    mutationFn: (body) => createWritOrder(body),
    meta: подтверждение('Постановление пристава заведено'),
    onSuccess: сбросить,
  });
}
